import { useMemo } from 'react';
import { IPublishFormValues, Steps } from '../../types';
import { useExternalSystems } from '../../../../hooks/useExternalSystems';
import { IntegrationType } from '../../../../constants';
import { VenueAllocationForm } from 'src/components/VenueAllocationModal';
import { useVenueCategories } from 'src/hooks/useVenueCategories';
import { useVenues } from 'src/hooks/useVenues/useVenues';

export const SelectVenues = ({
  submitDataToParent,
  defaultValues,
}: {
  submitDataToParent: (data: Partial<IPublishFormValues>, nextStep?: Steps) => void;
  defaultValues: Pick<IPublishFormValues, 'venues' | 'priceSchemeId'>;
}) => {
  const { data: externalSystems, isLoading: isExternalSystemsLoading } = useExternalSystems();
  const { data: allVenues = [], isLoading: isVenuesLoading } = useVenues();
  const { nestedCategories, orderedFlatCategories, isLoading: isCategoriesLoading } = useVenueCategories();

  // Filter venues to only show those from the POS system
  const posVenues = useMemo(() => {
    const posVenueIds =
      externalSystems
        ?.find((externalSystem) => externalSystem.integrationType === IntegrationType.POINT_OF_SALE_MANAGEMENT)
        ?.venues?.map((v) => v.venue_id) || [];

    return allVenues.filter((venue) => posVenueIds.includes(venue.venue_id));
  }, [externalSystems, allVenues]);

  const handleVenueSubmit = ({ venueIds }: { categoryId: string; venueIds: string[] }) => {
    submitDataToParent({ venues: venueIds }, Steps.excludeProducts);
  };

  const handleBack = () => {
    submitDataToParent({}, Steps.scheme);
  };

  const isLoading = isExternalSystemsLoading || isVenuesLoading || isCategoriesLoading;

  return (
    <VenueAllocationForm
      entityId="price-scheme"
      entityName="Price Scheme"
      venues={posVenues}
      selectedVenueIds={defaultValues.venues}
      onModalClose={handleBack}
      onSubmit={handleVenueSubmit}
      isLoading={isLoading}
      venueCategories={orderedFlatCategories || []}
      nestedVenueCategories={nestedCategories || []}
      title="Price Scheme Destination"
      subtitle="Choose which venues will receive the pricing data from the selected price scheme. Venue selection applies only to pricing."
      cancelButtonLabel="Back"
      submitButtonLabel="Continue"
      disableSubmitWhenEmpty
    />
  );
};
